import React from "react";
import "../../../AllComponentCSS.css";
import CodeCopyBlock from "../../../CodeCopyBlock";

// 🔹 Reusable Try It Button
function TryItButton({ code }) {
  const handleClick = () => {
    const encodedCode = encodeURIComponent(code);
    window.open(`/editorJS?code=${encodedCode}`, "_blank");
  };

  return (
    <button onClick={handleClick} className="try_it_button">
      ▶ Try It
    </button>
  );
}

export default function JS_Variables() {
  return (
    <div className="tutorial-container">
      {/* Title */}
      <h1 className="tutorial-title">💾 JavaScript Variables</h1>

      {/* Intro */}
      <p className="tutorial-intro">
        JavaScript <b>variables</b> are containers for storing data values. They
        make it possible to reuse and update information throughout your
        program. You can create variables using <code>var</code>,{" "}
        <code>let</code>, or <code>const</code>.
      </p>

      {/* Key Points */}
      <div
        className="Key_point_container"
        style={{
          background: "linear-gradient(135deg, #f0f9ff, #e0f2fe)",
        }}
      >
        <h2 style={{ color: "#075985" }}>🔑 Key Points</h2>
        <ul className="tutorial-list-points">
          <li>
            Use <code>let</code> for variables that can change.
          </li>
          <li>
            Use <code>const</code> for values that should not change.
          </li>
          <li>
            Avoid <code>var</code> — it’s older and can cause issues.
          </li>
          <li>
            Variable names must start with a letter, <code>$</code>, or{" "}
            <code>_</code>.
          </li>
          <li>
            JavaScript is case-sensitive — <code>age</code> and <code>Age</code>{" "}
            are different variables.
          </li>
        </ul>
      </div>

      {/* Example 1: Declaring Variables */}
      <div className="tutorial-section">
        <h2 className="section-title">1️⃣ Declaring Variables</h2>
        <p>
          You can declare variables using <code>let</code> or <code>const</code>
          .
        </p>

        <div className="code-demo-box">
          <CodeCopyBlock
            code={`let name = "Edu-Tech Master Academy";
let age = 22;
const country = "India";

console.log(name);
console.log(age);
console.log(country);`}
          />
          <TryItButton
            code={`let name = "Edu-Tech Master Academy";
let age = 22;
const country = "India";

console.log(name);
console.log(age);
console.log(country);`}
          />
        </div>

        <div
          style={{
            marginTop: "1rem",
            fontSize: "1.2rem",
            padding: "10px",
            border: "2px dashed #3b82f6",
            borderRadius: "8px",
          }}
        >
          Output:
          <pre>
            Edu-Tech Master Academy
            <br />
            22
            <br />
            India
          </pre>
        </div>
      </div>

      {/* Example 2: Changing Variable Values */}
      <div className="tutorial-section">
        <h2 className="section-title">2️⃣ Changing Variable Values</h2>
        <p>
          You can update variables declared with <code>let</code>, but not those
          declared with <code>const</code>.
        </p>

        <div className="code-demo-box">
          <CodeCopyBlock
            code={`let name = "Edu-Tech Master Academy";
console.log(name);

name = "ETMA JavaScript Course";
console.log(name);`}
          />
          <TryItButton
            code={`let name = "Edu-Tech Master Academy";
console.log(name);

name = "ETMA JavaScript Course";
console.log(name);`}
          />
        </div>

        <div
          style={{
            marginTop: "1rem",
            fontSize: "1.2rem",
            padding: "10px",
            border: "2px dashed #16a34a",
            borderRadius: "8px",
          }}
        >
          Output:
          <pre>
            Edu-Tech Master Academy
            <br />
            ETMA JavaScript Course
          </pre>
        </div>
      </div>

      {/* Example 3: Multiple Variable Declaration */}
      <div className="tutorial-section">
        <h2 className="section-title">3️⃣ Multiple Variable Declaration</h2>
        <p>You can declare and assign multiple variables in a single line.</p>

        <div className="code-demo-box">
          <CodeCopyBlock
            code={`let course = "JavaScript", duration = "4 weeks", level = "Beginner";
console.log(course, duration, level);`}
          />
          <TryItButton
            code={`let course = "JavaScript", duration = "4 weeks", level = "Beginner";
console.log(course, duration, level);`}
          />
        </div>

        <div
          style={{
            marginTop: "1rem",
            fontSize: "1.2rem",
            padding: "10px",
            border: "2px dashed #f59e0b",
            borderRadius: "8px",
          }}
        >
          Output: <b>JavaScript 4 weeks Beginner</b>
        </div>
      </div>

      {/* Example 4: Undefined Variables */}
      <div className="tutorial-section">
        <h2 className="section-title">4️⃣ Undefined Variables</h2>
        <p>
          A variable declared but not assigned a value has the value{" "}
          <code>undefined</code>.
        </p>

        <div className="code-demo-box">
          <CodeCopyBlock
            code={`let score;
console.log(score); // undefined`}
          />
          <TryItButton
            code={`let score;
console.log(score);`}
          />
        </div>

        <div
          style={{
            marginTop: "1rem",
            fontSize: "1.2rem",
            padding: "10px",
            border: "2px dashed #ef4444",
            borderRadius: "8px",
          }}
        >
          Output: <b>undefined</b>
        </div>
      </div>

      {/* Example 5: Dynamic Typing */}
      <div className="tutorial-section">
        <h2 className="section-title">5️⃣ Dynamic Typing</h2>
        <p>
          JavaScript is <b>dynamically typed</b>, meaning you can change a
          variable’s data type at runtime.
        </p>

        <div className="code-demo-box">
          <CodeCopyBlock
            code={`let data = "Edu-Tech Master Academy"; // string
console.log(typeof data);

data = 2025; // number
console.log(typeof data);`}
          />
          <TryItButton
            code={`let data = "Edu-Tech Master Academy";
console.log(typeof data);

data = 2025;
console.log(typeof data);`}
          />
        </div>

        <div
          style={{
            marginTop: "1rem",
            fontSize: "1.2rem",
            padding: "10px",
            border: "2px dashed #9333ea",
            borderRadius: "8px",
          }}
        >
          Output:
          <pre>
            string
            <br />
            number
          </pre>
        </div>
      </div>

      {/* Example 6: Using const with Objects */}
      <div className="tutorial-section">
        <h2 className="section-title">6️⃣ Using const with Objects</h2>
        <p>
          You can’t reassign a <code>const</code> variable, but you can modify
          the properties of a <b>const object</b>.
        </p>

        <div className="code-demo-box">
          <CodeCopyBlock
            code={`const academy = { name: "Edu-Tech Master Academy", course: "JavaScript" };
console.log(academy);

academy.course = "ReactJS"; // ✅ allowed
console.log(academy);

// academy = { city: "Delhi" }; // ❌ Error`}
          />
          <TryItButton
            code={`const academy = { name: "Edu-Tech Master Academy", course: "JavaScript" };
console.log(academy);

academy.course = "ReactJS";
console.log(academy);`}
          />
        </div>

        <div
          style={{
            marginTop: "1rem",
            fontSize: "1.2rem",
            padding: "10px",
            border: "2px dashed #2563eb",
            borderRadius: "8px",
          }}
        >
          Output:
          <pre>
            {"{ name: 'Edu-Tech Master Academy', course: 'JavaScript' }"}
            <br />
            {"{ name: 'Edu-Tech Master Academy', course: 'ReactJS' }"}
          </pre>
        </div>
      </div>

      {/* Best Practices */}
      <div
        style={{
          background: "linear-gradient(135deg, #fef9c3, #fde68a)",
          padding: "1rem",
          paddingLeft: "35px",
          borderRadius: "12px",
          marginTop: "2rem",
        }}
      >
        <h2 style={{ color: "#92400e" }}>🧠 Best Practices</h2>
        <ul className="tutorial-list-points">
          <li>
            Use <code>const</code> by default; switch to <code>let</code> when
            reassignment is needed.
          </li>
          <li>Always initialize variables when declaring them.</li>
          <li>
            Use meaningful names (e.g., <code>academyName</code>,{" "}
            <code>courseLevel</code>).
          </li>
          <li>
            Avoid using <code>var</code> in modern JavaScript.
          </li>
        </ul>
      </div>

      {/* Next Steps */}
      <div
        style={{
          background: "linear-gradient(135deg, #fef3c7, #fde68a)",
          padding: "1rem",
          paddingLeft: "35px",
          borderRadius: "12px",
          marginTop: "2rem",
        }}
      >
        <h2 style={{ color: "#92400e" }}>🚀 Next Steps</h2>
        <ul className="tutorial-list-points">
          <li>
            Learn about <b>JavaScript Data Types</b>.
          </li>
          <li>
            Understand how <b>operators</b> work with variables.
          </li>
          <li>
            Practice writing programs using <b>const</b> and <b>let</b>.
          </li>
        </ul>
      </div>
    </div>
  );
}
